// A HOME NOVA (repaginação de 30/10/2026): só a leitura e a trilha.
// O livro de agora (capa, progresso e a próxima parada da Travessia, com o
// último comentário aberto como isca), registrar em 1 toque e a entrada do
// mapa. Os outros livros em andamento: uma fileira discreta. Sem livro: um
// começo que leva à Temporada (Explorar).
// O que saiu daqui foi para o Perfil, o Clube ou o Explorar (ver o readout).
import React, { useEffect, useMemo, useState } from 'react';
import type { BookV2, StreakData } from '../../types';
import { api, type Curadoria, type TravessiaResumo } from '../../services/apiV2';
import { BookCover } from '../common';
import { Icon } from '../icons';
import { CardPro } from './pro';
import './nova.css';

const FOCO = 'forjamente.nova.foco';
const DIAS = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];
const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

/** O rótulo de um botão com a contagem das novidades: "Travessia · 24 novas"
 *  numa linha só. Abaixo de 420 px o CSS esconde o "· 24 novas" (fica para o
 *  leitor de tela) e mostra o número num selo ember. */
export const NvRotuloNovas: React.FC<{ texto: string; n: number }> = ({ texto, n }) => (
  <>
    <span className="nv-rotulo-novas">{texto}{n > 0 && <span className="nv-novas-txt">{` · ${n} ${n === 1 ? 'nova' : 'novas'}`}</span>}</span>
    {n > 0 && <span className="nv-novas-selo" aria-hidden="true" data-novas-selo={n}>{n}</span>}
  </>
);

/** A capa da interface nova: a do livro da estante (com o desenho de reserva) ou a do acervo. */
export const NvCapa: React.FC<{ book?: BookV2 | null; src?: string | null; titulo?: string; w: number; h: number; className?: string }> = ({ book, src, titulo, w, h, className }) => (
  <div className={`nv-capa ${className || ''}`} style={w ? { width: w, height: h } : undefined}>
    {book && w ? <BookCover book={book} width={w} height={h} />
      : (src || (book && book.coverUrl && book.coverUrl !== 'null')) ? <img src={(src || book!.coverUrl)!} alt={titulo || book?.title || ''} loading="lazy" />
      : book ? <span className="nv-capa-reserva nv-serif" style={{ background: `linear-gradient(135deg, oklch(0.62 0.09 ${book.coverHue ?? 20}), oklch(0.48 0.10 ${book.coverHue ?? 20}))` }}>{book.title}</span>
      : null}
  </div>
);

const pctDe = (b: BookV2) => (b.pages > 0 ? Math.min(100, Math.round(((b.read || 0) / b.pages) * 100)) : 0);
const ultimaAtividade = (b: BookV2) => {
  const s = (b.sessions || []).map((x) => x.date).sort().pop();
  return s || b.updatedAt || b.startedAt || b.createdAt || '';
};

/** A sequência de dias, pequena: um ponto aceso e o número. */
export const Sequencia: React.FC<{ streak: StreakData | null }> = ({ streak }) => {
  const n = streak?.current || 0;
  return (
    <span className={`nv-sequencia nv-mono ${n ? '' : 'apagada'}`} data-sequencia={n}>
      <i aria-hidden="true" />{n ? `${n} ${n === 1 ? 'dia seguido' : 'dias seguidos'}` : 'Comece a sequência hoje'}
    </span>
  );
};

export const HomeNova: React.FC<{
  session: any;
  books: BookV2[];
  streak: StreakData | null;
  travessias: Record<string, TravessiaResumo>;
  carregando?: boolean;
  onRegistrar: (b: BookV2) => void;
  onTravessia: (b: BookV2) => void;
  /** "Ontem você levou": abre a Travessia com a folha daquele insight/desafio. */
  onAbrirItem?: (b: BookV2, itemId: string) => void;
  onAbrirLivro: (b: BookV2) => void;
  onExplorar: () => void;
  onAdicionarLivro: () => void;
  /** O caderno do livro (na trilha, quando ele tem travessia). */
  onCaderno?: (b: BookV2) => void;
  /** Esta conta assina o Pro? Sem ele, aparece o cartão "Torne-se Pro". */
  pro?: boolean;
  onPro?: () => void;
  /** O aviso do teste grátis (bloqueio-pro.tsx): no teste, vai no lugar do "Torne-se Pro". */
  aviso?: React.ReactNode;
}> = ({ session, books, streak, travessias, carregando, onRegistrar, onTravessia, onAbrirItem, onAbrirLivro, onExplorar, onAdicionarLivro, onCaderno, pro = true, onPro, aviso }) => {
  const lendo = useMemo(() => books.filter((b) => b.status === 'reading'), [books]);
  const [foco, setFoco] = useState<string | null>(() => { try { return localStorage.getItem(FOCO); } catch { return null; } });

  // O livro de agora: o que a pessoa escolheu na fileira; senão o de leitura
  // mais recente, preferindo o que tem Travessia.
  const atual = useMemo(() => {
    const escolhido = lendo.find((b) => b.id === foco);
    if (escolhido) return escolhido;
    return [...lendo].sort((a, b) =>
      (Number(!!travessias[b.id]) - Number(!!travessias[a.id])) || ultimaAtividade(b).localeCompare(ultimaAtividade(a)))[0] || null;
  }, [lendo, foco, travessias]);
  const outros = lendo.filter((b) => b.id !== atual?.id);
  const escolher = (b: BookV2) => {
    setFoco(b.id);
    try { localStorage.setItem(FOCO, b.id); } catch {}
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
  };

  const hoje = new Date();
  const data = `${DIAS[hoje.getDay()]} · ${hoje.getDate()} ${MESES[hoje.getMonth()]}`;

  if (carregando && !books.length) return <div className="nv" data-tela="home-nova" />;

  return (
    <div className="nv" data-tela="home-nova">
      <div className="nv-topo nv-entra">
        <span className="nv-marca" data-marca>
          <img src="/icons/forjamente-icon.png" alt="" />
          <span><b>Forjamente</b><small>{data}</small></span>
        </span>
        <Sequencia streak={streak} />
      </div>
      {atual ? (
        <LivroDeAgora key={atual.id} b={atual} t={travessias[atual.id]} onRegistrar={onRegistrar} onTravessia={onTravessia} onAbrirItem={onAbrirItem} onAbrirLivro={onAbrirLivro} onExplorar={onExplorar} onCaderno={onCaderno} />
      ) : (
        <Comeco session={session} onExplorar={onExplorar} onAdicionarLivro={onAdicionarLivro} temLivros={books.length > 0} onPro={!pro && !aviso ? onPro : undefined} />
      )}
      {aviso ? <div className="nv-entra nv-entra-4">{aviso}</div>
        : atual && !pro && onPro && <div className="nv-entra nv-entra-4"><CardPro onAbrir={onPro} onde="home" compacto /></div>}
      {outros.length > 0 && (
        <section className="nv-tambem nv-entra nv-entra-4" data-tambem-lendo={outros.length}>
          <div className="nv-rotulo nv-mono"><span>Também lendo</span><span>{outros.length}</span></div>
          <div className="nv-fileira">
            {outros.map((b) => (
              <button key={b.id} className="nv-mini" onClick={() => escolher(b)} aria-label={`Trazer ${b.title} para o centro`}>
                <NvCapa book={b} w={74} h={111} />
                <span className="nv-mini-pct nv-mono">{pctDe(b)}%{travessias[b.id] ? ' · mapa' : ''}</span>
                <span className="nv-mini-titulo">{b.title}</span>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

const LivroDeAgora: React.FC<{
  b: BookV2; t?: TravessiaResumo;
  onRegistrar: (b: BookV2) => void; onTravessia: (b: BookV2) => void; onAbrirLivro: (b: BookV2) => void; onExplorar: () => void;
  onAbrirItem?: (b: BookV2, itemId: string) => void;
  onCaderno?: (b: BookV2) => void;
}> = ({ b, t, onRegistrar, onTravessia, onAbrirItem, onAbrirLivro, onExplorar, onCaderno }) => {
  const pct = pctDe(b);
  const emPaginas = !b.progressUnit || b.progressUnit === 'pages';
  const unidade = emPaginas ? 'p.' : b.progressUnit === 'chapters' ? 'cap.' : 'pos.';
  const [cheio, setCheio] = useState(false);
  useEffect(() => { const r = requestAnimationFrame(() => setCheio(true)); return () => cancelAnimationFrame(r); }, []);
  const proxima = t?.proxima || null;
  const faltam = proxima && t?.pagina != null ? Math.max(0, proxima.pagina - t.pagina) : null;

  return (
    <>
      <section className="nv-palco" data-livro-atual={b.id}>
        <button className="nv-capa-btn nv-entra" onClick={() => onAbrirLivro(b)} aria-label={`Abrir a página de ${b.title}`}>
          <NvCapa book={b} w={124} h={186} />
        </button>
        <h1 className="nv-livro-titulo nv-serif nv-entra nv-entra-2">{b.title}</h1>
        <span className="nv-livro-autor nv-mono nv-entra nv-entra-2">{b.author}</span>

        <div className="nv-progresso nv-entra nv-entra-3" data-progresso-home>
          <div className="nv-progresso-linha nv-mono">
            <span><b>{unidade} {b.read || 0}</b> de {b.pages || '—'}</span>
            <span><b>{pct}%</b></span>
          </div>
          <div className="nv-filete" aria-hidden="true"><span style={{ transform: `scaleX(${cheio ? pct / 100 : 0})` }} /></div>
        </div>

        {t ? (
          <button className="nv-parada nv-entra nv-entra-3" onClick={() => onTravessia(b)} data-proxima-parada={proxima ? proxima.pagina : 'fim'}>
            <div className="nv-parada-rotulo nv-mono">
              <span>{t.concluida ? 'Travessia concluída' : 'Próxima parada'}</span>
              {t.etapa && <span>Etapa {t.etapa.n}/{t.etapas} · {t.etapa.nome}</span>}
            </div>
            {proxima ? (
              <p className="nv-parada-frase nv-serif">p. {proxima.pagina} <em>· algo te espera</em></p>
            ) : (
              <p className="nv-parada-frase nv-serif">{t.concluida ? <>O caminho inteiro <em>está aberto.</em></> : <>Você chegou <em>ao fim do que já se abriu.</em></>}</p>
            )}
            {faltam != null && faltam > 0 && <small className="nv-mono" style={{ color: 'var(--ink-3)', display: 'block', marginTop: 6 }}>Faltam {faltam} {faltam === 1 ? 'página' : 'páginas'}</small>}
            {t.ultimo && (
              <div className="nv-isca" data-isca>
                <p className="nv-serif">“{t.ultimo.texto}”</p>
                <small className="nv-mono">O que se abriu na p. {t.ultimo.pagina}</small>
              </div>
            )}
          </button>
        ) : null}

        <div className="nv-acoes nv-entra nv-entra-4">
          {/* O que ela levou da visita anterior (insight ou desafio liberado antes de hoje). */}
          {t?.levou && onAbrirItem && (
            <button className="nv-levou" onClick={() => onAbrirItem(b, t.levou!.id)} data-levou={t.levou.id} data-levou-forma={t.levou.forma}
              aria-label={`${t.levou.ontem ? 'Ontem' : 'Da última vez'} você levou: ${t.levou.titulo}. Abrir o ${t.levou.forma}`}>
              <span className="nv-mono">{t.levou.ontem ? 'Ontem' : 'Da última vez'} você levou:</span> <b className="nv-mono" data-levou-titulo>{t.levou.titulo}</b>
            </button>
          )}
          <button className="nv-primario" onClick={() => onRegistrar(b)} data-registrar>
            <Icon name="pen" size={18} /> Registrar páginas
          </button>
          {t ? (
            <div className={onCaderno ? 'nv-dupla' : undefined}>
              <button className="nv-secundario" onClick={() => onTravessia(b)} data-abrir-travessia>
                <Icon name="globe" size={17} /> <NvRotuloNovas texto={onCaderno ? 'Travessia' : 'Abrir a Travessia'} n={t.nao_vistos || 0} />
              </button>
              {onCaderno && (
                <button className="nv-secundario" onClick={() => onCaderno(b)} data-abrir-caderno>
                  <Icon name="book" size={17} /> <NvRotuloNovas texto="Caderno" n={0} />
                </button>
              )}
            </div>
          ) : (
            <>
              {onCaderno && (
                <button className="nv-secundario" onClick={() => onCaderno(b)} data-abrir-caderno>
                  <Icon name="book" size={17} /> Caderno do livro
                </button>
              )}
              <button className="nv-link" onClick={onExplorar} data-sem-travessia>
                Este livro ainda não tem trilha · ver a temporada
              </button>
            </>
          )}
        </div>
      </section>
    </>
  );
};

/** Sem livro em andamento: a porta da Temporada. */
const Comeco: React.FC<{ session: any; onExplorar: () => void; onAdicionarLivro: () => void; temLivros: boolean; onPro?: () => void }> = ({ session, onExplorar, onAdicionarLivro, temLivros, onPro }) => {
  const [cur, setCur] = useState<Curadoria | null>(null);
  useEffect(() => {
    let vivo = true;
    api.curadoria(session).then((c) => { if (vivo) setCur(c); }).catch(() => {});
    return () => { vivo = false; };
  }, [session]);
  const capas = (cur?.temporada.livros || []).filter((l) => l.capa).slice(0, 3);
  // A do meio é a do lançamento (o livro com travessia disponível).
  const lanc = capas.find((l) => l.travessia === 'disponivel');
  const ordem = lanc ? [capas.find((l) => l !== lanc), lanc, capas.filter((l) => l !== lanc)[1]].filter(Boolean) as typeof capas : capas;

  return (
    <section className="nv-comeco" data-comeco>
      <div className="nv-leque nv-entra" aria-hidden="true">
        {ordem.map((l) => <NvCapa key={l.slug} src={l.capa} titulo={l.titulo} w={112} h={168} />)}
      </div>
      <span className="nv-mono nv-entra nv-entra-2" style={{ color: 'var(--ember)' }}>{cur ? `${cur.temporada.nome} · ${cur.temporada.titulo}` : 'Temporada 1'}</span>
      <h1 className="nv-serif nv-entra nv-entra-2">Ler é o começo. <em>Aplicar</em> é o caminho.</h1>
      <p className="nv-sub nv-entra nv-entra-3">
        {temLivros ? 'Nenhum livro em andamento agora. ' : ''}Escolha um livro da temporada: você lê no seu ritmo, faz os desafios e o caminho se abre a cada página.
      </p>
      <div className="nv-acoes nv-entra nv-entra-4">
        <button className="nv-primario" onClick={onExplorar} data-ver-temporada>Ver a Temporada <Icon name="arrow-right" size={18} /></button>
        <button className="nv-link" onClick={onAdicionarLivro}>Já estou lendo outro livro · registrar</button>
      </div>
      {onPro && <div className="nv-entra nv-entra-4" style={{ textAlign: 'left' }}><CardPro onAbrir={onPro} onde="comeco" capas={(cur?.temporada.livros || []).map((l) => l.capa)} /></div>}
    </section>
  );
};
